import React, { useState, useEffect, useCallback } from 'react';
import Button from "@mui/material/Button";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

/**
 * AccordionNextButton component for navigation in accordion panels
 *
 * @param {Object} props Component props
 * @param {Function} props.onClick Function to call when the button is clicked. If it's an async operation, it should return a Promise.
 * @param {boolean} props.disabled Whether the button is disabled
 * @param {string} props.errorMessage Error message to display below the button
 * @param {number} props.panelIndex The index of this accordion panel
 * @param {number} props.activePanel The currently active panel in the accordion
 */
const AccordionNextButton = ({
  onClick,
  disabled = false,
  errorMessage = '',
  panelIndex,
  activePanel
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  // Determine if this button should respond to the hotkey based on active panel
  const isActivePanel = Array.isArray(activePanel)
  ? activePanel[0] === panelIndex
  : activePanel === panelIndex;

  // Memoize handleClick to stabilize its identity for useEffect dependency.
  // It depends on onClick, disabled, and isProcessing props.
  const handleClick = useCallback(async () => { // MODIFIED: Made async
    if (isProcessing || disabled) return;

    setIsProcessing(true);
    try {
      if (onClick) { // MODIFIED: Check if onClick is defined before calling
        await onClick(); // MODIFIED: Await the onClick function passed from the parent
      }
    } catch (error) {
      // This catches errors from the panel's onClick if not caught internally by the panel.
      // The panel should ideally handle its specific errors.
      console.error('Error during AccordionNextButton onClick execution:', error);
    } finally {
      // MODIFIED: Reset processing state immediately after onClick finishes or errors.
      setIsProcessing(false);
    }
  }, [onClick, disabled, isProcessing]); // Dependencies for useCallback

  // Effect to add and remove global keydown event listener for Ctrl+Enter / Meta+Enter
  useEffect(() => {
    // Only add the event listener if this panel's button should be active for hotkeys
    if (!isActivePanel) return;

    const handleKeyDown = async (event) => { // MODIFIED: Made async to await handleClick
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        // handleClick internally checks for 'disabled' and 'isProcessing' states
        event.preventDefault(); // MODIFIED: Moved preventDefault and stopPropagation earlier
        event.stopPropagation();
        await handleClick(); // MODIFIED: Added await
      }
    };

    // Add global event listener to the document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function to remove the event listener when the component unmounts
    // or when dependencies change, leading to the effect re-running.
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  // Dependencies for useEffect:
  // - isActivePanel: to ensure listener is only active for the current panel.
  // - handleClick: to ensure the listener always uses the latest version of handleClick,
  //   which itself depends on `onClick`, `disabled`, and `isProcessing`.
  }, [isActivePanel, handleClick]);

  return (
    <div style={{ marginTop: '16px' }}>
      <Button
        variant="contained"
        color="primary"
        endIcon={<KeyboardArrowRightIcon />}
        onClick={handleClick} // Use the memoized handleClick for the button's click event
        disabled={disabled || isProcessing} // Button is disabled if explicitly passed or if processing
        tabIndex={-1} // Remove from tab order since it has Ctrl+Enter hotkey
        style={{
          backgroundColor: '#337AB7',
          width: '240px' // Increased width to accommodate longer text
        }}
      >
        {/* Display hotkey hint if this panel is active */}
        NEXT {isActivePanel ? "(Ctrl + Enter)" : ""}
      </Button>
      {/* Display error messages if any, splitting by newline for multiple messages */}
      {errorMessage && errorMessage.split('\n').map((msg, i) => (
        <div
          key={i}
          style={{
            marginTop: '8px',
            color: '#f44336', // Standard error text color
            fontSize: '0.875rem'
          }}
        >
          {msg}
        </div>
      ))}
    </div>
  );
};

export default AccordionNextButton;